import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { IQuestionnaireQuestion } from 'src/app/interfaces/Questionnaire/IQuestionnaire';
import { EpisodeOfCareService } from 'src/app/services/episodeOfCare.service';
import { PermissionService } from 'src/app/services/permission.service';

@Component({
  selector: 'app-fast-protocol-mediterranean-questions',
  templateUrl: './fast-protocol-mediterranean-questions.component.html',
  styleUrl: './fast-protocol-mediterranean-questions.component.css',
  standalone: false
})
export class FastProtocolMediterraneanQuestionsComponent implements OnInit {
  @Input() elements: IQuestionnaireQuestion[] = [];
  @Input() formGroup!: FormGroup;

  constructor(
    private fb: FormBuilder,
    private readonly eocService: EpisodeOfCareService,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
    private readonly translate: TranslateService,
    private readonly permissionService: PermissionService
  ) {}

  isInpatientView(): boolean {
    return (
      !(
        this.eocService.currentEocId() === null ||
        this.eocService.currentEocId() === ''
      ) && this.router.url.includes('inPatientView')
    );
  }

  isEOC() {
    return (
      !(
        this.eocService.currentEocId() === null ||
        this.eocService.currentEocId() === ''
      ) && this.router.url.includes('episodeOfCare')
    );
  }

  hasPermission(permission: string[]): boolean {
    return this.permissionService.can(permission);
  }

  ngOnInit() {}

  setNotMentioned(fieldName: string): void {
    this.formGroup
      .get(fieldName)
      ?.setValue(this.translate.instant('Not mentioned'));
    this.cdr.detectChanges(); // Ensure UI updates immediately
  }

  // Put the real question names here:
  private toggleRules: Record<string, { normalDisables?: string[]; abnormalDisables?: string[] }> = {
    question40: { normalDisables: ['question41', 'question47'] },
    question41: { abnormalDisables: ['question40'] },
    question42: { normalDisables: ['question43', 'question48']},
    question43: { abnormalDisables: ['question42']},
    question44: { normalDisables: ['question46']},
    question46: { abnormalDisables: ['question44']},
  };

  private norm(v: any): string {
    return String(v ?? '').trim().toLowerCase();
  }

  private isNormalSelected(element: any, value: any): boolean {
    const selected = element?.choices?.find((c: any) => c.value === value);
    const text = selected?.text ?? '';
    return this.norm(text) === this.norm(this.translate.instant('Normal'));
  }

  private isAbnormalSelected(element: any, value: any): boolean {
    const selected = element?.choices?.find((c: any) => c.value === value);
    const text = selected?.text ?? '';
    return this.norm(text) === this.norm(this.translate.instant('Abnormal'));
  }

  onRadioClick(controlName: string, value: any, element?: any): void {
    const control = this.formGroup.get(controlName);
    if (!control) return;

    // toggle off
    if (control.value === value) {
      control.setValue(null);
      this.enableAllRelated(controlName);
      this.cdr.detectChanges();
      return;
    }

    control.setValue(value);

    // Apply cross-question rules
    const rules = this.toggleRules[controlName];
    if (!rules) {
      this.cdr.detectChanges();
      return;
    }

    // First reset related controls (so switching Normal<->Abnormal works cleanly)
    this.enableAllRelated(controlName);

    if (this.isNormalSelected(element, value) && rules.normalDisables?.length) {
      for (const target of rules.normalDisables) this.disableAndClear(target);
    }

    if (this.isAbnormalSelected(element, value) && rules.abnormalDisables?.length) {
      for (const target of rules.abnormalDisables) this.disableAndClear(target);
    }

    this.cdr.detectChanges();
  }

  private disableAndClear(controlName: string): void {
    const c = this.formGroup.get(controlName);
    if (!c) return;
    c.setValue(null, { emitEvent: false });
    c.disable({ emitEvent: false });
  }

  private enable(controlName: string): void {
    const c = this.formGroup.get(controlName);
    if (!c) return;
    c.enable({ emitEvent: false });
  }

  private enableAllRelated(source: string): void {
    const rules = this.toggleRules[source];
    if (!rules) return;

    const all = [...(rules.normalDisables ?? []), ...(rules.abnormalDisables ?? [])];
    for (const target of all) this.enable(target);
  }

}
